var kind      = require("enyo/kind"),
    Component = require("enyo/Component");

/**
 * [ComponentTaskQueue]{@link enyo-perf.ComponentTaskQueue} is a helper that
 * allows it's owner to easily defer expensive method calls and execute them in
 * order at a later time.
 *
 * @class   enyo-perf.ComponentTaskQueue
 * @extends enyo.Component
 * @public
 */
module.exports = kind(/** @lends enyo-perf.ComponentTaskQueue.prototype */ {

    /**
     * @private
     */
    name: "enyo-perf.ComponentTaskQueue",

    /**
     * @private
     */
    kind: Component,

    /**
     * The deferred tasks in method => arguments pairs.
     *
     * @type {Object}
     * @private
     */
    queue: null,

    /**
     * The amount of deferred tasks.
     *
     * @type {number}
     * @readonly
     * @public
     */
    length: null,

    /**
     * Sets up a queue for deferred tasks.
     *
     * @method
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            this.queue  = {};
            this.length = 0;
            sup.apply(this, arguments);
        };
    }),

    /**
     * Guard method, will be executed before every task when the queue gets
     * flushed. By default the guard always returns `true`. Override it if you
     * need to interrupt the queue's execution for any particular reason.
     *
     * @return  {boolean}  Allows execution of the next task in the queue if
     *                     boolean `true`, defers execution of all currently
     *                     queued tasks otherwise.
     * @default `true`
     * @protected
     */
    guard: function() {
        return true;
    },

    /**
     * Sets a new [guard]{@link enyo-perf.ComponentTaskQueue#guard} method which
     * will be executed before each task once the queue gets [flushed]{@link
     * enyo-perf.ComponentTaskQueue#flush}.
     *
     * If you need the guard method to execute in a particular context, bind the
     * guard to its context before setting it.
     *
     * @param {Function}  guard  A guard method
     * @public
     */
    setGuard: function(guard) {
        this.guard = guard;
    },

    /**
     * Add a task to the [queue]{@link enyo-perf.ComponentTaskQueue#queue}.
     *
     * @param {string}  method  The name of the deferred method
     * @param {Array=}  args    The arguments to pass to the deferred method.
     * @public
     */
    addTask: function(method, args) {
        if (!this.queue.hasOwnProperty(method)) {
            ++this.length;
        }

        this.queue[method] = args || [];
    },

    /**
     * Removes a task from the [queue]{@link
     * enyo-perf.ComponentTaskQueue#queue}.
     *
     * @param {string}  method  The name of the deferred method
     * @public
     */
    removeTask: function(method) {
        if (this.queue.hasOwnProperty(method)) {
            delete this.queue[method];
            --this.length;
        }
    },

    /**
     * Executes registered tasks one by one as long as the configured
     * [guard]{@link enyo-perf.ComponentTaskQueue#guard} method returns a
     * boolean `true`. Flushing is interrupted as soon as the [guard]{@link
     * enyo-perf.ComponentTaskQueue#guard} return anything else.
     *
     * @public
     */
    flush: function() {
        var queue = this.queue,
            guard = this.guard,
            owner = this.owner,
            method;

        for (method in queue) {
            if (guard() === true) {
                owner[method].apply(owner, queue[method]);
                this.removeTask(method);
            }
            else {
                break;
            }
        }
    },

    /**
     * Resets the [queue]{@link enyo-perf.ComponentTaskQueue#queue}. Queued
     * tasks will not be fired.
     *
     * @public
     */
    clear: function() {
        this.queue  = {};
        this.length = 0;
    },

    /**
     * Clear the [queue]{@link enyo-perf.ComponentTaskQueue#queue} when control
     * gets destroyed.
     *
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.clear();
            sup.apply(this, arguments);
        };
    })

});
